import { createFileRoute, Link } from "@tanstack/react-router";
import { LegalPage, LegalSection } from "@/components/LegalPage";
import { ObfuscatedEmail } from "@/components/ObfuscatedEmail";
import { COMPANY } from "@/data/company";
import { apriPreferenzeCookie } from "@/lib/consent";

// Informativa riscritta il 29/10/2026 su quello che il sito fa davvero:
// - modulo Contatti → funzione `api` su Supabase → notifica nel gruppo Telegram della bottega (non salvato nel database);
// - modulo Richieste → tabella `reservations` su Supabase (Irlanda) + notifica Telegram;
// - hosting e pagine su Netlify; font ospitati sul sito;
// - Google Analytics 4 e Google Maps solo dopo il consenso del banner (src/lib/consent.ts).
// Se cambia uno di questi strumenti, va cambiato anche questo testo (e la cookie policy).

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy · La Bottega delle Carni da Marco" },
      { name: "description", content: "Informativa sul trattamento dei dati personali ai sensi degli artt. 13 e 14 del Regolamento UE 2016/679 (GDPR) per chi visita il sito o ci scrive." },
      { name: "robots", content: "index,follow" },
      { property: "og:title", content: "Privacy Policy · La Bottega delle Carni da Marco" },
      { property: "og:description", content: "Come trattiamo i dati personali raccolti tramite il sito, il modulo contatti e le richieste di disponibilità." },
      { property: "og:url", content: "https://labottegadellecarnidamarco.it/privacy" },
    ],
    links: [{ rel: "canonical", href: "https://labottegadellecarnidamarco.it/privacy" }],
  }),
  component: PrivacyPage,
});

const link = "text-brand-red hover:underline";

function PrivacyPage() {
  return (
    <LegalPage
      eyebrow="Informativa privacy"
      title="Privacy Policy"
      intro="Trasparenza, prima di tutto: ecco come trattiamo i tuoi dati."
      updated="29 ottobre 2026"
    >
      <p className="text-brand-charcoal/85">
        Informativa ai sensi degli articoli 13 e 14 del Regolamento (UE) 2016/679 (GDPR) per chi visita
        questo sito, ci scrive dal modulo Contatti o ci manda una richiesta di disponibilità.
      </p>

      <LegalSection title="1. Titolare del trattamento">
        <p>
          Il titolare è <strong>{COMPANY.ragioneSociale}</strong>, sede legale in {COMPANY.sedeLegale},
          P. IVA e codice fiscale {COMPANY.piva}. La bottega si trova in {COMPANY.sedeOperativa}.
        </p>
        <p>
          Per qualsiasi domanda sui tuoi dati scrivi a <ObfuscatedEmail className={link} /> oppure
          chiama il <a className={link} href="tel:+393407487310">{COMPANY.telefono}</a>.
        </p>
      </LegalSection>

      <LegalSection title="2. Quali dati trattiamo">
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>Modulo Contatti</strong>: nome, email, telefono se lo lasci e il testo del messaggio.
          </li>
          <li>
            <strong>Richieste di disponibilità</strong> (pagina Richieste): nome, cognome, telefono, email se
            la lasci, i prodotti che ti interessano, la data in cui vorresti ritirarli e le note. Finché non
            invii la richiesta, i prodotti scelti restano solo nel tuo browser.
          </li>
          <li>
            <strong>Navigazione</strong>: come ogni sito, il server che lo ospita riceve dati tecnici (indirizzo
            IP, tipo di browser, pagine richieste, data e ora) che servono a mostrarti le pagine e a proteggerle
            dagli attacchi.
          </li>
          <li>
            <strong>Statistiche</strong>, solo se le accetti nel banner dei cookie: pagine visitate, durata della
            visita, sito di provenienza, tipo di dispositivo e posizione approssimativa, raccolti da Google
            Analytics.
          </li>
          <li>
            Se ci scrivi su <strong>WhatsApp</strong>, per email o ci chiami, trattiamo i dati che ci dai in quel
            modo, per le stesse finalità.
          </li>
        </ul>
        <p>
          Nelle note non servono dati sulla salute. Se ci segnali un'allergia o un'intolleranza, la usiamo
          solo per preparare o sconsigliarti i prodotti richiesti e la cancelliamo insieme alla richiesta.
        </p>
      </LegalSection>

      <LegalSection title="3. Perché li usiamo e su quale base">
        <ul className="list-disc pl-6 space-y-2">
          <li>
            Rispondere ai messaggi, verificare la disponibilità dei prodotti e concordare con te il ritiro in
            bottega. Base giuridica: misure precontrattuali adottate su tua richiesta (art. 6.1.b GDPR).
          </li>
          <li>
            Se acquisti in bottega, adempiere agli obblighi fiscali e contabili (art. 6.1.c GDPR).
          </li>
          <li>
            Proteggere il sito e farlo funzionare, sulla base del nostro legittimo interesse (art. 6.1.f GDPR).
          </li>
          <li>
            Statistiche delle visite con Google Analytics e mappa interattiva di Google Maps: solo con il tuo
            consenso (art. 6.1.a GDPR e art. 122 del Codice privacy), che puoi revocare quando vuoi da
            «<button type="button" onClick={apriPreferenzeCookie} className={link}>Preferenze cookie</button>».
          </li>
        </ul>
        <p>
          Non usiamo i tuoi dati per pubblicità, newsletter o profilazione e non li vendiamo. Darci i dati è
          facoltativo, ma senza nome e un recapito non possiamo risponderti. Non prendiamo decisioni
          automatizzate sul tuo conto.
        </p>
      </LegalSection>

      <LegalSection title="4. Per quanto tempo">
        <ul className="list-disc pl-6 space-y-2">
          <li>
            Richieste e messaggi (nel database del sito e nelle notifiche ricevute dalla bottega): per il tempo
            necessario a gestirli e comunque non oltre 24 mesi dall'invio.
          </li>
          <li>Se nasce una vendita, i documenti fiscali si conservano per 10 anni, come prevede la legge.</li>
          <li>Statistiche di Google Analytics: per il periodo impostato nel servizio, al massimo 14 mesi.</li>
          <li>Dati tecnici di navigazione: per il tempo strettamente necessario al funzionamento e alla sicurezza del sito.</li>
        </ul>
      </LegalSection>

      <LegalSection title="5. Chi tratta i dati per nostro conto">
        <p>Usiamo pochi fornitori, che vedono i dati solo per fare il loro lavoro:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Netlify</strong> (Netlify, Inc., Stati Uniti): ospita il sito e invia le pagine.</li>
          <li>
            <strong>Supabase</strong> (Supabase, Inc., Stati Uniti): riceve i moduli e conserva le richieste di
            disponibilità in un database su server nell'Unione europea (Irlanda).
          </li>
          <li>
            <strong>Telegram</strong>: ci avvisa di ogni messaggio e di ogni richiesta con una notifica in un
            gruppo privato della bottega. Secondo la sua informativa i dati di chi si è registrato dall'Unione
            europea sono conservati in data center nei Paesi Bassi; il suo rappresentante nell'Unione è EDPO
            (Bruxelles).
          </li>
          <li>
            <strong>Google</strong> (Google Ireland Limited e Google LLC): Google Analytics per le statistiche,
            solo con il tuo consenso. Se attivi la mappa, Google Maps tratta i dati come titolare autonomo
            secondo la{" "}
            <a className={link} href="https://policies.google.com/privacy?hl=it" target="_blank" rel="noopener noreferrer">sua informativa</a>.
          </li>
          <li>
            <strong>Zofia Digital Agency</strong>, che ha realizzato il sito e ne cura la manutenzione, il database
            e le notifiche.
          </li>
          <li>Il consulente fiscale e contabile, per gli obblighi di legge legati alle vendite.</li>
        </ul>
        <p>Comunichiamo i dati alle autorità solo quando la legge lo impone.</p>
      </LegalSection>

      <LegalSection title="6. Trasferimenti fuori dall'Unione europea">
        <p>
          Netlify, Supabase e Google sono società con sede negli Stati Uniti: anche se il database si trova in
          Irlanda, possono trattare alcuni dati fuori dall'Unione europea. Lo fanno con le garanzie previste
          dal GDPR (artt. 45 e 46): il Data Privacy Framework UE-USA per le società che vi aderiscono e le
          clausole contrattuali standard approvate dalla Commissione europea. Telegram ha sede fuori
          dall'Unione europea; le notifiche contengono solo i dati che ci invii con i moduli.
        </p>
      </LegalSection>

      <LegalSection title="7. Cookie">
        <p>
          Senza il tuo consenso usiamo solo strumenti tecnici. Google Analytics e Google Maps partono solo
          se li accetti. Tutti i dettagli sono nella{" "}
          <Link to="/cookie" className={link}>Cookie Policy</Link>.
        </p>
      </LegalSection>

      <LegalSection title="8. I tuoi diritti">
        <p>
          Puoi chiederci in ogni momento di vedere i tuoi dati e averne copia, correggerli, cancellarli,
          limitarne l'uso o riceverli in un formato leggibile, e puoi opporti al trattamento basato sul nostro
          legittimo interesse. Il consenso dato si può revocare quando vuoi, senza effetti su quanto fatto
          prima. Scrivi a <ObfuscatedEmail className={link} />: ti rispondiamo entro un mese.
        </p>
        <p>
          Se pensi che il trattamento non sia corretto puoi presentare reclamo al Garante per la protezione
          dei dati personali (
          <a className={link} href="https://www.garanteprivacy.it" target="_blank" rel="noopener noreferrer">garanteprivacy.it</a>).
        </p>
        <p>Il sito non è rivolto ai minori di 14 anni.</p>
      </LegalSection>

      <LegalSection title="9. Modifiche">
        <p>
          Se cambiano i servizi che usiamo aggiorniamo questa pagina e la data in alto. Ultimo aggiornamento:
          29 ottobre 2026.
        </p>
      </LegalSection>
    </LegalPage>
  );
}
